/**
 * Viewing and downloading private files (28 Oct 2026). The documents bucket is
 * private, so every file is opened through a short-lived signed link that is
 * fetched first. Both helpers take that fetch as a function.
 */

/**
 * Opens the file in a new tab. The tab is opened straight away, before the
 * link is fetched, because browsers block a new tab opened after a wait.
 */
export async function viewFile(getUrl: () => Promise<string>): Promise<void> {
  const tab = window.open('', '_blank')
  try {
    const url = await getUrl()
    if (tab) tab.location.href = url
    else window.location.href = url
  } catch (error) {
    tab?.close()
    throw error
  }
}

/** Saves the file to Downloads under a readable name. */
export async function downloadFile(getUrl: () => Promise<string>, filename: string): Promise<void> {
  const url = await getUrl()
  const response = await fetch(url)
  if (!response.ok) throw new Error('The file could not be downloaded. Please try again.')
  const blob = await response.blob()
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(href), 10_000)
}

/** "Natasha & Marko – Quote 12.pdf": safe as a filename everywhere. */
export function fileName(...parts: string[]): string {
  return parts
    .filter(Boolean)
    .join(' – ')
    .replace(/[\\/:*?"<>|]/g, '')
    .trim()
}

const UNITS = ['B', 'KB', 'MB', 'GB']

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return ''
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${UNITS[unit]}`
}
